import { useTranslation } from "react-i18next";
import { Button, CardNotification, Link, Table, TableColumn } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import type { SessionAssignationRow } from "@services/types/session-assignation";
import {
  canCreateOccurrence,
  canDeleteOccurrence,
} from "@services/collections/session-assignation";
import type {
  SessionAssignationCreation,
  SessionAssignationEditingDraft,
} from "../../types/session-assignation";
import {
  canEditOccurrence,
  isOccurrenceProtected,
  isOccurrenceDateAllowed,
} from "../../utils/session-assignation";
import SessionResourceCell from "../session-resource-cell";
import SessionDateCell from "../session-date-cell";
import EditableTableCell from "../editable-table-cell";
import SessionTableTooltip from "../session-table-tooltip";
import styles from "./sessionAssignationTable.module.scss";

type TableRow =
  | { row: SessionAssignationRow; creation?: never }
  | { row?: never; creation: SessionAssignationCreation };

interface SessionAssignationTableProps {
  draft: SessionAssignationEditingDraft;
  canEditInstructors?: boolean;
  rows: SessionAssignationRow[];
  selectedIds: string[];
  creation: SessionAssignationCreation | null;
  onSelect: (ids: string[], checked: boolean) => void;
  onDate: (rowId: string, date: string) => void;
  onCreationDate: (date: string) => void;
  onTime: (row: SessionAssignationRow | null) => void;
  onResources: (field: "classrooms" | "instructors", row: SessionAssignationRow | null) => void;
  onUndoDeletion: (rowId: string) => void;
  onCancelCreation: () => void;
}

const SessionAssignationTable = ({
  draft,
  rows,
  selectedIds,
  creation,
  canEditInstructors = true,
  onSelect,
  onDate,
  onCreationDate,
  onTime,
  onResources,
  onUndoDeletion,
  onCancelCreation,
}: SessionAssignationTableProps) => {
  const { t, i18n } = useTranslation();
  const canCreate = draft.phase === "editing" && canCreateOccurrence(draft.policy);
  const data: TableRow[] = [...(creation ? [{ creation }] : []), ...rows.map(row => ({ row }))];
  const columns: TableColumn[] = [
    {
      key: "date",
      header: t("assignation-edit.session-view.table.date"),
      desktopOptions: { headCellStyles: { minWidth: "200px" } },
      renderCell: raw => {
        const { row, creation: newRow } = raw as TableRow;
        const values = row ?? newRow;
        const editable = row ? canEditOccurrence(row, draft) : canCreate;
        const disabled = !editable || !draft.policy.abilities.can_assign_intervals;
        const week = draft.weeks.find(item => item.id === row?.intervalId);
        const weekday =
          row?.scheduleMode === "scheduled"
            ? new Intl.DateTimeFormat(i18n.language, { weekday: "long", timeZone: "UTC" }).format(
                new Date(`${row.date}T00:00:00Z`),
              )
            : null;
        return (
          <div
            className={`${styles.date} ${!row || row.pendingState === "new" ? styles.newRow : ""}`}
          >
            <SessionDateCell
              date={values.date}
              disabled={disabled}
              description={
                week
                  ? `${t("assignation-edit.session-view.week", {
                      week: week.name ?? draft.weeks.indexOf(week) + 1,
                    })}${weekday ? ` · ${weekday}` : ""}`
                  : undefined
              }
              isDateAllowed={date => isOccurrenceDateAllowed(date, draft, values)}
              onChange={date => (row ? onDate(row.localId, date) : onCreationDate(date))}
            />
          </div>
        );
      },
    },
    {
      key: "time",
      header: t("assignation-edit.session-view.table.time"),
      desktopOptions: { headCellStyles: { minWidth: "160px" } },
      renderCell: raw => {
        const { row, creation: newRow } = raw as TableRow;
        const values = row ?? newRow;
        const disabled =
          !(row ? canEditOccurrence(row, draft) : canCreate) ||
          !draft.policy.abilities.can_assign_blocks;
        const durations = new Set(
          draft.blocks
            .filter(
              block => row?.scheduleMode === "unscheduled" || row?.blocksIds.includes(block.id),
            )
            .map(block => {
              const minutes = (time: string) =>
                Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
              return minutes(block.endingTime) - minutes(block.startingTime);
            }),
        );
        const duration = durations.size === 1 ? [...durations][0] : null;
        const label =
          values.scheduleMode === "scheduled"
            ? `${values.startingTime?.slice(0, 5) ?? "—"} - ${values.endingTime?.slice(0, 5) ?? "—"}`
            : values.scheduleMode === "unscheduled"
              ? t("assignation-edit.session-view.unscheduled", {
                  blocks: values.blocksCountInput || "—",
                })
              : t("assignation-edit.session-view.define-time");
        if (!values.scheduleMode)
          return (
            <Link iconLeft="plus" disabled={disabled} onClick={() => onTime(row ?? null)}>
              {label}
            </Link>
          );
        return (
          <SessionTableTooltip
            label={t("assignation-edit.session-view.block-duration", { minutes: duration })}
            disabled={!duration}
            focusable={disabled}
          >
            <EditableTableCell disabled={disabled} onClick={() => onTime(row ?? null)}>
              {label}
            </EditableTableCell>
          </SessionTableTooltip>
        );
      },
    },
    ...(["classrooms", "instructors"] as const).map(field => ({
      key: field,
      header: t(`assignation-edit.session-view.table.${field}`),
      desktopOptions: { headCellStyles: { minWidth: "230px" } },
      renderCell: (raw: unknown) => {
        const { row, creation: newRow } = raw as TableRow;
        const values = row ?? newRow;
        const disabled =
          !(row ? canEditOccurrence(row, draft) : canCreate) ||
          !draft.policy.abilities[`can_assign_${field}`] ||
          (field === "instructors" && !canEditInstructors);
        return (
          <SessionResourceCell
            items={values[field]}
            disabled={disabled}
            emptyLabel={t(`assignation-edit.session-view.without-${field}`)}
            onClick={() => onResources(field, row ?? null)}
          />
        );
      },
    })),
  ];

  if (!data.length)
    return (
      <CardNotification
        state="info"
        title={t("assignation-edit.session-view.empty.title")}
        className={styles.empty}
        outlined
      >
        {t("assignation-edit.session-view.empty.description")}
      </CardNotification>
    );
  return (
    <Table
      className={styles.table}
      columns={columns}
      data={data}
      rowOptions={{
        selectorType: "checkbox",
        isSelected: raw =>
          !!(raw as TableRow).row && selectedIds.includes((raw as TableRow).row.localId),
        isSelectorDisabled: raw =>
          !(raw as TableRow).row ||
          draft.phase !== "editing" ||
          isOccurrenceProtected((raw as TableRow).row, draft),
        isHeadSelectorDisabled:
          !rows.some(row => !isOccurrenceProtected(row, draft)) || draft.phase !== "editing",
        isAllSelected: rows
          .filter(row => !isOccurrenceProtected(row, draft))
          .every(row => selectedIds.includes(row.localId)),
        onRowSelect: (checked, raw) => onSelect([(raw as TableRow).row.localId], checked),
        onSelectAll: checked =>
          onSelect(
            rows.filter(row => !isOccurrenceProtected(row, draft)).map(row => row.localId),
            checked,
          ),
      }}
      rightActions={[
        {
          renderContent: raw => {
            const { row, creation: newRow } = raw as TableRow;
            if (newRow)
              return (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancelCreation}
                  aria-label={t("assignation-edit.session-view.cancel-creation")}
                  leftIcon="cross"
                >
                  {t("generals.cancel")}
                </Button>
              );
            if (row.pendingState === "unchanged") return null;
            const icon =
              row.pendingState === "new"
                ? "circle-plus"
                : row.pendingState === "deleted"
                  ? "trash"
                  : "repeat";
            const label = t(`assignation-edit.session-view.pending.${row.pendingState}`);
            return row.pendingState === "deleted" ? (
              <SessionTableTooltip label={t("assignation-edit.session-view.undo-deletion")}>
                <button
                  type="button"
                  className={styles.pending}
                  disabled={
                    draft.phase !== "editing" ||
                    !canDeleteOccurrence(draft.policy) ||
                    isOccurrenceProtected(row, draft)
                  }
                  onClick={() => onUndoDeletion(row.localId)}
                  aria-label={t("assignation-edit.session-view.undo-deletion")}
                >
                  <Icon name={icon} size="sm" />
                </button>
              </SessionTableTooltip>
            ) : (
              <SessionTableTooltip label={label} focusable>
                <span className={styles.pending}>
                  <Icon name={icon} size="sm" />
                </span>
              </SessionTableTooltip>
            );
          },
        },
      ]}
    />
  );
};

export default SessionAssignationTable;
